
import { useState, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Cliente, concessionarias, Gerador } from "@/types";
import { ClienteService, GeradorService } from "@/services/StorageService";
import CepInput from "@/components/address/CepInput";
import { X, Save } from "lucide-react";
import { clienteFormSchema, ClienteFormValues } from "./form/types";

interface ClienteEditFormProps {
  cliente: Cliente;
  onSave: (updatedCliente: Cliente) => void;
  onCancel: () => void;
}

const ClienteEditForm = ({ cliente, onSave, onCancel }: ClienteEditFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [gerador, setGerador] = useState<Gerador | null>(null);
  
  useEffect(() => {
    // Buscar o gerador associado ao cliente
    if (cliente.geradorId) {
      const geradorData = GeradorService.getById(cliente.geradorId);
      setGerador(geradorData || null);
    }
  }, [cliente.geradorId]);
  
  const form = useForm<ClienteFormValues>({
    resolver: zodResolver(clienteFormSchema),
    defaultValues: {
      geradorId: cliente.geradorId,
      nome: cliente.nome,
      documento: cliente.documento,
      cep: cliente.cep,
      logradouro: cliente.logradouro,
      bairro: cliente.bairro,
      cidade: cliente.cidade,
      estado: cliente.estado,
      concessionaria: cliente.concessionaria,
      numeroCliente: cliente.numeroCliente,
      numeroInstalacao: cliente.numeroInstalacao,
      gastoEnergia: cliente.gastoEnergia,
      tarifaEnergia: cliente.tarifaEnergia
    }
  });
  
  // Calculated values based on form inputs
  const consumoMedio = form.watch("gastoEnergia") / form.watch("tarifaEnergia") || 0;
  
  const economiaEstimada = gerador 
    ? consumoMedio * (form.watch("tarifaEnergia") - gerador.tarifaVenda) 
    : 0;
  
  const percentualEconomia = form.watch("gastoEnergia") > 0 
    ? (economiaEstimada / form.watch("gastoEnergia")) * 100 
    : 0;
    
  // Calcular o percentual da energia do gerador que está sendo adquirida
  const percentualEnergiaAdquirida = gerador && gerador.geracaoEstimada > 0
    ? (consumoMedio / gerador.geracaoEstimada) * 100
    : 0;

  const onSubmit = async (values: ClienteFormValues) => {
    setIsSubmitting(true);
    try {
      // Verificar se há mudança no consumo que possa afetar os créditos
      const updatedCliente = ClienteService.update(cliente.id, values);
      
      if (updatedCliente) {
        toast.success("Cliente atualizado com sucesso!");
        onSave(updatedCliente);
      } else {
        toast.error("Erro ao atualizar cliente. Verifique se há créditos suficientes disponíveis.");
      }
    } catch (error) {
      console.error("Erro ao atualizar cliente:", error);
      toast.error("Erro ao atualizar cliente");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCepFound = (address: any) => {
    form.setValue("cep", address.cep);
    form.setValue("logradouro", address.logradouro);
    form.setValue("bairro", address.bairro);
    form.setValue("cidade", address.localidade);
    form.setValue("estado", address.uf);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-energy-blue-dark">Editar Cliente</h2>
        <div className="flex space-x-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={onCancel}
            className="border-muted-foreground text-muted-foreground"
          >
            <X className="h-4 w-4 mr-2" />
            Cancelar
          </Button>
          <Button 
            size="sm"
            className="bg-energy-blue text-white hover:bg-energy-blue-dark"
            onClick={form.handleSubmit(onSubmit)}
            disabled={isSubmitting}
          >
            <Save className="h-4 w-4 mr-2" />
            {isSubmitting ? "Salvando..." : "Salvar"}
          </Button>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Dados do Gerador (somente leitura) */}
          {gerador && (
            <div className="p-4 bg-energy-green-dark/10 rounded-md">
              <h3 className="font-medium mb-2">Gerador Associado</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Nome</p>
                  <p className="font-medium">{gerador.nomeTitular}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Tarifa de Venda</p>
                  <p className="font-medium">R$ {gerador.tarifaVenda.toFixed(4)}/kWh</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Créditos Disponíveis</p>
                  <p className="font-medium">
                    {gerador.creditosDisponiveis.toFixed(2)} kWh/mês
                  </p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Nota: O gerador associado não pode ser alterado. Para alterar o gerador, é necessário excluir este cliente e criar um novo.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="nome"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome do Cliente</FormLabel>
                  <FormControl>
                    <Input placeholder="Nome completo" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="documento"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>CPF/CNPJ</FormLabel>
                  <FormControl>
                    <Input placeholder="CPF ou CNPJ" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="cep"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>CEP</FormLabel>
                  <FormControl>
                    <div className="flex space-x-2">
                      <Input {...field} placeholder="CEP" className="flex-1" />
                      <CepInput onAddressFound={handleCepFound} />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="logradouro"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Logradouro</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Rua, Avenida, etc" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="bairro"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bairro</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Bairro" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="cidade"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cidade</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Cidade" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="estado"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Estado</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="UF" maxLength={2} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="concessionaria"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Concessionária</FormLabel>
                  <Select 
                    onValueChange={field.onChange} 
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a concessionária" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {concessionarias.map((concessionaria) => (
                        <SelectItem 
                          key={concessionaria} 
                          value={concessionaria}
                        >
                          {concessionaria}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="numeroCliente"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Número do Cliente</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Número do cliente" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="numeroInstalacao"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Número da Instalação</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Número da instalação" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="gastoEnergia"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Gasto com Energia (R$/mês)</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      step="0.01" 
                      min="0"
                      placeholder="0.00" 
                      {...field}
                      onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="tarifaEnergia"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tarifa de Energia (R$/kWh)</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      step="0.0001" 
                      min="0"
                      placeholder="0.0000" 
                      {...field}
                      onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Campos calculados */}
          <div className="bg-muted p-4 rounded-md space-y-2 mt-6">
            <h3 className="font-semibold text-lg text-energy-blue-dark">Valores Calculados</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Consumo Estimado (kWh/mês)</p>
                <p className="font-medium text-lg">
                  {consumoMedio.toFixed(2)} kWh
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Economia Estimada (R$/mês)</p>
                <p className="font-medium text-lg text-energy-green">
                  R$ {economiaEstimada.toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Percentual de Economia</p>
                <p className="font-medium text-lg text-energy-green">
                  {percentualEconomia.toFixed(1)}%
                </p>
              </div>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default ClienteEditForm;
